import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Search, Eye, Trash2, User, Mail, Calendar } from 'lucide-react';
import { agentApi } from '@/services/agentApi';

export const StudentProfilesPage: React.FC = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadProfiles();
  }, [currentPage, searchTerm]);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const result = await agentApi.getStudentProfiles({
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      });
      setProfiles(result.profiles || []);
      setTotalPages(result.pages);
      setTotal(result.total);
    } catch (error) {
      console.error('Error loading profiles:', error);
      toast.error('Failed to load student profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadProfiles();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this student profile?')) {
      try {
        await agentApi.deleteStudentProfile(id);
        loadProfiles();
        toast.success('Student profile deleted successfully');
      } catch (error: any) {
        console.error('Error deleting profile:', error);
        toast.error(error?.response?.data?.error || 'Failed to delete profile');
      }
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h4 fw-bold text-dark mb-1">Student Profiles</h1>
              <p className="text-muted small mb-0">
                Manage saved student profiles for quick application creation
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-8">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Search size={18} className="position-absolute end-0 top-50 translate-middle-y me-3 text-muted" />
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleSearch}
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Profiles Table */}
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : profiles.length === 0 ? (
                <div className="text-center py-5">
                  <User size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No student profiles found</h5>
                  <p className="text-muted small">
                    {searchTerm
                      ? 'Try adjusting your search terms'
                      : 'Student profiles will appear here after you save them from applications'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Date of Birth</th>
                          <th>Country</th>
                          <th>Last Updated</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profiles.map((profile) => (
                          <motion.tr
                            key={profile.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="cursor-pointer"
                            onClick={() => navigate(`/student-profiles/${profile.id}`)}
                          >
                            <td>
                              <div className="d-flex align-items-center">
                                <User size={18} className="text-muted me-2" />
                                <div>
                                  <div className="fw-semibold">
                                    {profile.firstName} {profile.middleName} {profile.lastName}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Mail size={14} className="text-muted me-2" />
                                {profile.email}
                              </div>
                            </td>
                            <td>
                              {profile.dateOfBirth ? (
                                <div className="d-flex align-items-center">
                                  <Calendar size={14} className="text-muted me-2" />
                                  {new Date(profile.dateOfBirth).toLocaleDateString()}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{profile.countryOfCitizenship || <span className="text-muted">-</span>}</td>
                            <td>
                              {profile.updatedAt
                                ? new Date(profile.updatedAt).toLocaleDateString()
                                : '-'}
                            </td>
                            <td className="text-end">
                              <div className="btn-group" onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => navigate(`/student-profiles/${profile.id}`)}
                                  title="View Details"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(profile.id)}
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="card-footer bg-white border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, total)} of {total} profiles
                        </div>
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                if (totalPages <= 7) return true;
                                return (
                                  page === 1 ||
                                  page === totalPages ||
                                  (page >= currentPage - 1 && page <= currentPage + 1)
                                );
                              })
                              .map((page, index, array) => (
                                <React.Fragment key={page}>
                                  {index > 0 && array[index - 1] !== page - 1 && (
                                    <li className="page-item disabled">
                                      <span className="page-link">...</span>
                                    </li>
                                  )}
                                  <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button
                                      className="page-link"
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                </React.Fragment>
                              ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

